import {combine, sample} from 'effector';
import {createGate} from 'effector-react';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {NotificationContentDto, NotificationMessageDto} from '@entities/notifications/types';
import {
    DictionarySearchResponse,
    ViolationDictionaryItem,
    ViolationDto,
} from '@entities/violations/types';

import * as api from './api';
import {ViolationHistoryDto, ViolationHistoryResponse} from './types';

const domain = rootDomain.createDomain('AuditViolation');

export const AuditViolationGate = createGate<{id: string}>('AuditViolation');

const fetchViolationFx = domain.createEffect({handler: api.fetchViolation});
const fetchViolationTypesFx = domain.createEffect({handler: api.fetchViolationTypes});
const fetchNotificationStatusesFx = domain.createEffect({handler: api.fetchNotificationStatuses});
const fetchViolationNotificationsFx = domain.createEffect({
    handler: api.fetchViolationNotifications,
});
const fetchViolationHistoryFx = domain.createEffect({handler: api.fetchViolationHistory});
const fetchViolationEventTypesFx = domain.createEffect({handler: api.fetchViolationEventTypes});
const fetchNotificationDetailsFx = domain.createEffect({handler: api.fetchNotificationDetails});
const downloadNotificationMessageFx = domain.createEffect({
    handler: api.downloadNotificationMessage,
});

[
    fetchViolationFx,
    fetchViolationTypesFx,
    fetchNotificationStatusesFx,
    fetchViolationNotificationsFx,
    fetchViolationHistoryFx,
    fetchViolationEventTypesFx,
    fetchNotificationDetailsFx,
    downloadNotificationMessageFx,
].forEach((fx) => fx.failData.watch(showRestError));

const violation$ = domain
    .createStore<ViolationDto | null>(null)
    .on(fetchViolationFx.doneData, (_, data) => data)
    .reset(AuditViolationGate.close);

const violationTypes$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(fetchViolationTypesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(AuditViolationGate.close);

const notificationStatuses$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(
        fetchNotificationStatusesFx.doneData,
        (_, data: DictionarySearchResponse) => data.data ?? [],
    )
    .reset(AuditViolationGate.close);

const notifications$ = domain
    .createStore<Awaited<ReturnType<typeof api.fetchViolationNotifications>> | null>(null)
    .on(fetchViolationNotificationsFx.doneData, (_, data) => data)
    .reset(AuditViolationGate.close);

const history$ = domain
    .createStore<ViolationHistoryResponse | null>(null)
    .on(fetchViolationHistoryFx.doneData, (_, data: ViolationHistoryResponse) => data)
    .reset(AuditViolationGate.close);

const eventTypes$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(fetchViolationEventTypesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(AuditViolationGate.close);

const historyEventType$ = domain.createStore<string | null>(null).reset(AuditViolationGate.close);
const historyPageNumber$ = domain.createStore(0).reset(AuditViolationGate.close);
const historyPageSize$ = domain.createStore(10).reset(AuditViolationGate.close);
const selectedHistoryItem$ = domain
    .createStore<ViolationHistoryDto | null>(null)
    .reset(AuditViolationGate.close);
const selectedMessage$ = domain
    .createStore<NotificationMessageDto | null>(null)
    .on(fetchNotificationDetailsFx.doneData, (_, data) => data.message)
    .reset(AuditViolationGate.close);
const contentList$ = domain
    .createStore<NotificationContentDto[]>([])
    .on(fetchNotificationDetailsFx.doneData, (_, data) => data.contents)
    .reset(AuditViolationGate.close);
const isNotificationDetailsOpen$ = domain.createStore(false).reset(AuditViolationGate.close);

export const historyEventTypeChanged = domain.createEvent<string | null>();
export const historyPageNumberChanged = domain.createEvent<number>();
export const historyPageSizeChanged = domain.createEvent<number>();
export const historyDetailsOpened = domain.createEvent<ViolationHistoryDto>();
export const historyDetailsClosed = domain.createEvent();
export const latestNotificationOpened = domain.createEvent();
export const notificationDetailsClosed = domain.createEvent();
export const latestNotificationDownloadRequested = domain.createEvent();

historyEventType$.on(historyEventTypeChanged, (_, value) => value);
historyPageNumber$.on(historyPageNumberChanged, (_, value) => value);
historyPageSize$.on(historyPageSizeChanged, (_, value) => value);
historyPageNumber$.reset(historyEventTypeChanged, historyPageSizeChanged);
selectedHistoryItem$
    .on(historyDetailsOpened, (_, value) => value)
    .reset(historyDetailsClosed, AuditViolationGate.close);
isNotificationDetailsOpen$
    .on(latestNotificationOpened, () => true)
    .on(notificationDetailsClosed, () => false);
selectedMessage$.reset(notificationDetailsClosed);
contentList$.reset(notificationDetailsClosed);

sample({
    clock: AuditViolationGate.open,
    filter: ({id}) => Boolean(id),
    fn: ({id}) => id,
    target: fetchViolationFx,
});

sample({
    clock: AuditViolationGate.open,
    target: [fetchViolationTypesFx, fetchNotificationStatusesFx, fetchViolationEventTypesFx],
});

sample({
    clock: AuditViolationGate.open,
    filter: ({id}) => Boolean(id),
    fn: ({id}) => id,
    target: fetchViolationNotificationsFx,
});

sample({
    clock: [
        AuditViolationGate.open,
        historyEventTypeChanged,
        historyPageNumberChanged,
        historyPageSizeChanged,
    ],
    source: combine({
        gate: AuditViolationGate.state,
        eventType: historyEventType$,
        page: historyPageNumber$,
        size: historyPageSize$,
    }),
    filter: AuditViolationGate.status,
    fn: ({gate, eventType, page, size}) => ({id: gate.id, eventType, page, size}),
    target: fetchViolationHistoryFx,
});

sample({
    clock: latestNotificationOpened,
    source: notifications$,
    filter: (notifications) => Boolean(notifications?.data?.[0]?.id),
    fn: (notifications) => notifications!.data[0].id,
    target: fetchNotificationDetailsFx,
});

sample({
    clock: latestNotificationDownloadRequested,
    source: notifications$,
    filter: (notifications) => Boolean(notifications?.data?.[0]?.id),
    fn: (notifications) => notifications!.data[0].id,
    target: downloadNotificationMessageFx,
});

const pending$ = combine(
    [
        fetchViolationFx.pending,
        fetchViolationTypesFx.pending,
        fetchNotificationStatusesFx.pending,
        fetchViolationNotificationsFx.pending,
        fetchViolationEventTypesFx.pending,
    ],
    (list) => list.some(Boolean),
);

const $auditViolationState = combine({
    violation: violation$,
    violationTypes: violationTypes$,
    notificationStatuses: notificationStatuses$,
    notifications: notifications$,
    history: history$,
    eventTypes: eventTypes$,
    historyEventType: historyEventType$,
    historyPageNumber: historyPageNumber$,
    historyPageSize: historyPageSize$,
    selectedHistoryItem: selectedHistoryItem$,
    selectedMessage: selectedMessage$,
    contentList: contentList$,
    isNotificationDetailsOpen: isNotificationDetailsOpen$,
    pending: pending$,
    historyPending: fetchViolationHistoryFx.pending,
    notificationDetailsPending: combine(
        [fetchNotificationDetailsFx.pending, downloadNotificationMessageFx.pending],
        (list) => list.some(Boolean),
    ),
});

export const auditViolationModel = {
    state: $auditViolationState,
    historyEventTypeChanged,
    historyPageNumberChanged,
    historyPageSizeChanged,
    historyDetailsOpened,
    historyDetailsClosed,
    latestNotificationOpened,
    notificationDetailsClosed,
    latestNotificationDownloadRequested,
};
